import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Button,
  TextField,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Checkbox,
  FormGroup,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import StarIcon from '@material-ui/icons/Star';
import StarBorderIcon from '@material-ui/icons/StarBorder';
import CloseIcon from '@material-ui/icons/Close';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import { OnboardingLanguage } from '../../translations';
import { getOnboardingColors } from './colors';
import { NpsAnswers } from '../../api/onboardingApi';

/* ── i18n labels ──────────────────────────────────── */

/* ── stable keys for Q7 & Q8 (language-independent) ── */

const Q7_KEYS = ['yes_significantly', 'yes_somewhat', 'no_difference', 'cannot_assess'] as const;
const Q8_KEYS = ['modal_tooltip', 'videos', 'diagrams', 'quiz', 'playground'] as const;

type Q7Key = typeof Q7_KEYS[number];
type Q8Key = typeof Q8_KEYS[number];

interface NpsLabels {
  q1: string;
  q2: string;
  q3: string;
  q4: string;
  q5: string;
  q6: string;
  q7: string;
  q7Options: Record<Q7Key, string>;
  q8: string;
  q8Options: Record<Q8Key, string>;
  q9: string;
  q9Placeholder: string;
  q10: string;
  q10NotLikely: string;
  q10ExtremelyLikely: string;
  q11: string;
  q11Placeholder: string;
  submit: string;
  thankYou: string;
  thankYouSubtitle: string;
  thankYouClose: string;
  required: string;
}

const NPS_LABELS: Record<OnboardingLanguage, NpsLabels> = {
  en: {
    q1: '1. The explanations provided during the onboarding were clear and easy to understand.',
    q2: '2. The onboarding\'s duration and pace were appropriate.',
    q3: '3. The onboarding content is useful for my day-to-day work.',
    q4: '4. The videos and diagrams helped me understand the content.',
    q5: '5. The hands-on Playground experience (Journey 2) reinforced my learning.',
    q6: '6. After completing the onboarding, I feel confident using the IDP independently.',
    q7: '7. Did the onboarding reduce the time it would have taken me to figure out the IDP features on my own?',
    q7Options: { yes_significantly: 'Yes, significantly', yes_somewhat: 'Yes, somewhat', no_difference: 'It made no difference', cannot_assess: "I can't assess" },
    q8: '8. Which content formats were most valuable for your learning? (select all that apply)',
    q8Options: { modal_tooltip: 'Modal and tooltip texts', videos: 'Videos', diagrams: 'Diagrams', quiz: 'Quiz', playground: 'Playground (hands-on)' },
    q9: '9. What would you improve about the IDP onboarding? (optional)',
    q9Placeholder: 'Enter your answer',
    q10: '10. On a scale from 0 to 10, how likely are you to recommend the IDP onboarding to a fellow developer?',
    q10NotLikely: 'Not at all likely',
    q10ExtremelyLikely: 'Extremely likely',
    q11: '11. What is the main reason for your score? (optional)',
    q11Placeholder: 'Enter your answer',
    submit: 'Submit',
    thankYou: 'Thank you for your feedback!',
    thankYouSubtitle: 'Your answers have been recorded successfully.',
    thankYouClose: 'Close',
    required: '* Required',
  },
  'pt-BR': {
    q1: '1. As explicações fornecidas durante o onboarding foram claras e fáceis de entender.',
    q2: '2. A duração e o ritmo do onboarding foram apropriados.',
    q3: '3. O conteúdo do onboarding é útil para meu trabalho diário.',
    q4: '4. Os vídeos e diagramas me ajudaram a entender o conteúdo.',
    q5: '5. A experiência prática do Playground (Jornada 2) reforçou meu aprendizado.',
    q6: '6. Após concluir o onboarding, me sinto confiante para usar o IDP de forma independente.',
    q7: '7. O onboarding reduziu o tempo que você levaria para descobrir os recursos do IDP por conta própria?',
    q7Options: { yes_significantly: 'Sim, significativamente', yes_somewhat: 'Sim, um pouco', no_difference: 'Não fez diferença', cannot_assess: 'Não consigo avaliar' },
    q8: '8. Quais formatos de conteúdo foram mais valiosos para seu aprendizado? (selecione todos que se aplicam)',
    q8Options: { modal_tooltip: 'Textos em modais e tooltips', videos: 'Vídeos', diagrams: 'Diagramas', quiz: 'Quiz', playground: 'Playground (prática)' },
    q9: '9. O que você melhoraria no onboarding do IDP? (opcional)',
    q9Placeholder: 'Digite sua resposta',
    q10: '10. De 0 a 10, qual a probabilidade de recomendar o onboarding do IDP para um colega desenvolvedor?',
    q10NotLikely: 'Nada provável',
    q10ExtremelyLikely: 'Muito provável',
    q11: '11. Qual o principal motivo da sua nota? (opcional)',
    q11Placeholder: 'Digite sua resposta',
    submit: 'Enviar',
    thankYou: 'Obrigado pelo seu feedback!',
    thankYouSubtitle: 'Suas respostas foram registradas com sucesso.',
    thankYouClose: 'Fechar',
    required: '* Obrigatório',
  },
  es: {
    q1: '1. Las explicaciones proporcionadas durante el onboarding fueron claras y fáciles de entender.',
    q2: '2. La duración y el ritmo del onboarding fueron apropiados.',
    q3: '3. El contenido del onboarding es útil para mi trabajo diario.',
    q4: '4. Los videos y diagramas me ayudaron a entender el contenido.',
    q5: '5. La experiencia práctica del Playground (Jornada 2) reforzó mi aprendizaje.',
    q6: '6. Después de completar el onboarding, me siento seguro usando el IDP de forma independiente.',
    q7: '7. ¿El onboarding redujo el tiempo que te habría tomado descubrir las funcionalidades del IDP por tu cuenta?',
    q7Options: { yes_significantly: 'Sí, significativamente', yes_somewhat: 'Sí, un poco', no_difference: 'No hizo diferencia', cannot_assess: 'No puedo evaluar' },
    q8: '8. ¿Qué formatos de contenido fueron más valiosos para tu aprendizaje? (selecciona todos los que apliquen)',
    q8Options: { modal_tooltip: 'Textos en modales y tooltips', videos: 'Videos', diagrams: 'Diagramas', quiz: 'Quiz', playground: 'Playground (práctica)' },
    q9: '9. ¿Qué mejorarías del onboarding del IDP? (opcional)',
    q9Placeholder: 'Ingresa tu respuesta',
    q10: '10. Del 0 al 10, ¿qué tan probable es que recomiendes el onboarding del IDP a un colega desarrollador?',
    q10NotLikely: 'Nada probable',
    q10ExtremelyLikely: 'Muy probable',
    q11: '11. ¿Cuál es la razón principal de tu puntuación? (opcional)',
    q11Placeholder: 'Ingresa tu respuesta',
    submit: 'Enviar',
    thankYou: '¡Gracias por tu feedback!',
    thankYouSubtitle: 'Tus respuestas han sido registradas exitosamente.',
    thankYouClose: 'Cerrar',
    required: '* Obligatorio',
  },
};

/* ── styles ───────────────────────────────────────── */

const useStyles = makeStyles(theme => {
  const c = getOnboardingColors(theme);
  return {
    dialog: {
      '& .MuiDialog-paper': {
        borderRadius: 8,
        maxWidth: 700,
        width: '95vw',
        maxHeight: '90vh',
        margin: theme.spacing(2),
        overflow: 'hidden',
      },
    },
    header: {
      backgroundColor: c.modalHeaderBg,
      color: c.modalHeaderFg,
      padding: theme.spacing(1.5, 2),
      position: 'relative',
    },
    headerSubtitle: {
      fontSize: '0.875rem',
      lineHeight: '1.125rem',
      fontWeight: 400,
      opacity: 0.85,
      marginBottom: theme.spacing(1),
    },
    headerTitle: {
      fontSize: '1rem',
      lineHeight: '1.25rem',
      fontWeight: 700,
    },
    closeButton: {
      position: 'absolute',
      top: theme.spacing(2.5),
      right: theme.spacing(2),
      color: c.modalHeaderFg,
    },
    content: {
      padding: theme.spacing(3),
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(3),
      overflowY: 'auto',
    },
    questionBlock: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(1),
    },
    questionLabel: {
      fontWeight: 500,
      fontSize: '0.9rem',
    },
    requiredMark: {
      color: theme.palette.error.main,
      marginLeft: 4,
    },
    starRow: {
      display: 'flex',
      gap: 2,
    },
    starButton: {
      background: 'none',
      border: 'none',
      padding: 0,
      cursor: 'pointer',
      color: theme.palette.warning.main,
      fontSize: '1.75rem',
      lineHeight: 1,
      '&:hover': { opacity: 0.8 },
    },
    scoreContainer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: theme.spacing(1),
    },
    scoreButtons: {
      display: 'flex',
      gap: theme.spacing(0.5),
      flexWrap: 'wrap',
      justifyContent: 'center',
    },
    scoreButton: {
      minWidth: 36,
      height: 36,
      borderRadius: 4,
      border: `1px solid ${theme.palette.divider}`,
      backgroundColor: 'transparent',
      color: theme.palette.text.primary,
      cursor: 'pointer',
      fontSize: '0.875rem',
      fontWeight: 500,
      transition: 'all 0.2s',
      '&:hover': {
        backgroundColor: theme.palette.action.hover,
      },
    },
    scoreButtonSelected: {
      minWidth: 36,
      height: 36,
      borderRadius: 4,
      border: 'none',
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      cursor: 'pointer',
      fontSize: '0.875rem',
      fontWeight: 700,
      transition: 'all 0.2s',
    },
    scaleLabels: {
      display: 'flex',
      justifyContent: 'space-between',
      width: '100%',
      maxWidth: 440,
    },
    scaleLabel: {
      fontSize: '0.75rem',
      color: theme.palette.text.secondary,
    },
    submitButton: {
      alignSelf: 'flex-end',
      marginTop: theme.spacing(1),
      textTransform: 'none',
      fontWeight: 400,
    },
    thankYou: {
      textAlign: 'center',
      padding: theme.spacing(8, 4),
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: theme.spacing(2),
      minHeight: 300,
      minWidth: 500,
      justifyContent: 'center',
    },
    thankYouIcon: {
      fontSize: 64,
      color: c.success,
    },
    thankYouTitle: {
      fontWeight: 700,
      fontSize: '1.5rem',
      color: c.success,
    },
    thankYouSubtitle: {
      fontSize: '1rem',
      color: theme.palette.text.secondary,
      maxWidth: 400,
    },
    thankYouButton: {
      marginTop: theme.spacing(2),
      textTransform: 'none',
      fontWeight: 600,
      borderRadius: 4,
      padding: theme.spacing(1, 4),
    },
  };
});

/* ── component ────────────────────────────────────── */

interface NpsModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (answers: NpsAnswers) => Promise<boolean>;
  language: OnboardingLanguage;
  title: string;
  subtitle: string;
}

const StarRating = ({
  value,
  onChange,
  className,
}: {
  value: number;
  onChange: (v: number) => void;
  className: string;
}) => (
  <Box display="flex" style={{ gap: 2 }}>
    {[1, 2, 3, 4, 5].map(star => (
      <button
        key={star}
        type="button"
        className={className}
        onClick={() => onChange(star)}
        aria-label={`${star} star${star > 1 ? 's' : ''}`}
      >
        {star <= value ? <StarIcon fontSize="inherit" /> : <StarBorderIcon fontSize="inherit" />}
      </button>
    ))}
  </Box>
);

export const NpsModal = ({ open, onClose, onSubmit, language, title, subtitle }: NpsModalProps) => {
  const classes = useStyles();
  const labels = NPS_LABELS[language];

  const [q1, setQ1] = useState(0);
  const [q2, setQ2] = useState(0);
  const [q3, setQ3] = useState(0);
  const [q4, setQ4] = useState(0);
  const [q5, setQ5] = useState(0);
  const [q6, setQ6] = useState(0);
  const [q7, setQ7] = useState<Q7Key | ''>('');
  const [q8, setQ8] = useState<Q8Key[]>([]);
  const [q9, setQ9] = useState('');
  const [q10, setQ10] = useState<number | null>(null);
  const [q11, setQ11] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const toggleQ8 = (key: Q8Key) => {
    setQ8(prev =>
      prev.includes(key) ? prev.filter(o => o !== key) : [...prev, key],
    );
  };

  const isValid =
    q1 > 0 && q2 > 0 && q3 > 0 && q4 > 0 && q5 > 0 && q6 > 0 &&
    q7 !== '' && q8.length > 0 && q10 !== null;

  const handleSubmit = async () => {
    if (!isValid || q10 === null) return;
    setSubmitting(true);
    try {
      const success = await onSubmit({
        q1ClearExplanations: q1,
        q2DurationPace: q2,
        q3UsefulContent: q3,
        q4VideosDiagrams: q4,
        q5PlaygroundExperience: q5,
        q6ConfidentIndependently: q6,
        q7ReducedTime: q7,
        q8ContentFormats: q8,
        q9Improvements: q9 || undefined,
        q10NpsScore: q10,
        q11ScoreReason: q11 || undefined,
      });
      if (success) {
        setSubmitted(true);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setQ1(0); setQ2(0); setQ3(0); setQ4(0); setQ5(0); setQ6(0);
    setQ7(''); setQ8([]); setQ9(''); setQ10(null); setQ11('');
    setSubmitted(false);
    onClose();
  };

  const starQuestions: { label: string; value: number; set: (v: number) => void }[] = [
    { label: labels.q1, value: q1, set: setQ1 },
    { label: labels.q2, value: q2, set: setQ2 },
    { label: labels.q3, value: q3, set: setQ3 },
    { label: labels.q4, value: q4, set: setQ4 },
    { label: labels.q5, value: q5, set: setQ5 },
    { label: labels.q6, value: q6, set: setQ6 },
  ];

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      className={classes.dialog}
      maxWidth={false}
    >
      <Box className={classes.header}>
        <Typography className={classes.headerSubtitle}>{subtitle}</Typography>
        <Typography className={classes.headerTitle}>{title}</Typography>
        <IconButton
          className={classes.closeButton}
          onClick={handleClose}
          size="small"
          aria-label="close"
        >
          <CloseIcon />
        </IconButton>
      </Box>
      <DialogContent className={classes.content}>
        {submitted ? (
          <Box className={classes.thankYou}>
            <CheckCircleOutlineIcon className={classes.thankYouIcon} />
            <Typography className={classes.thankYouTitle}>
              {labels.thankYou}
            </Typography>
            <Typography className={classes.thankYouSubtitle}>
              {labels.thankYouSubtitle}
            </Typography>
            <Button
              variant="contained"
              color="primary"
              className={classes.thankYouButton}
              onClick={handleClose}
            >
              {labels.thankYouClose}
            </Button>
          </Box>
        ) : (
          <>
            {/* Questions 1-6: Star ratings */}
            {starQuestions.map(({ label, value, set }) => (
              <Box key={label} className={classes.questionBlock}>
                <Typography className={classes.questionLabel}>
                  {label}<span className={classes.requiredMark}>*</span>
                </Typography>
                <StarRating value={value} onChange={set} className={classes.starButton} />
              </Box>
            ))}

            {/* Question 7: Radio */}
            <Box className={classes.questionBlock}>
              <Typography className={classes.questionLabel}>
                {labels.q7}<span className={classes.requiredMark}>*</span>
              </Typography>
              <FormControl component="fieldset">
                <RadioGroup value={q7} onChange={e => setQ7(e.target.value as Q7Key)}>
                  {Q7_KEYS.map(key => (
                    <FormControlLabel
                      key={key}
                      value={key}
                      control={<Radio size="small" color="primary" />}
                      label={labels.q7Options[key]}
                    />
                  ))}
                </RadioGroup>
              </FormControl>
            </Box>

            {/* Question 8: Checkboxes */}
            <Box className={classes.questionBlock}>
              <Typography className={classes.questionLabel}>
                {labels.q8}<span className={classes.requiredMark}>*</span>
              </Typography>
              <FormControl component="fieldset">
                <FormGroup>
                  {Q8_KEYS.map(key => (
                    <FormControlLabel
                      key={key}
                      control={
                        <Checkbox
                          size="small"
                          color="primary"
                          checked={q8.includes(key)}
                          onChange={() => toggleQ8(key)}
                        />
                      }
                      label={labels.q8Options[key]}
                    />
                  ))}
                </FormGroup>
              </FormControl>
            </Box>

            {/* Question 9: Free text (optional) */}
            <Box className={classes.questionBlock}>
              <Typography className={classes.questionLabel}>{labels.q9}</Typography>
              <TextField
                multiline
                minRows={2}
                maxRows={4}
                variant="outlined"
                placeholder={labels.q9Placeholder}
                value={q9}
                onChange={e => setQ9(e.target.value)}
                fullWidth
              />
            </Box>

            {/* Question 10: NPS 0-10 */}
            <Box className={classes.questionBlock}>
              <Typography className={classes.questionLabel}>
                {labels.q10}<span className={classes.requiredMark}>*</span>
              </Typography>
              <Box className={classes.scoreContainer}>
                <Box className={classes.scoreButtons}>
                  {Array.from({ length: 11 }, (_, i) => (
                    <button
                      key={i}
                      type="button"
                      className={q10 === i ? classes.scoreButtonSelected : classes.scoreButton}
                      onClick={() => setQ10(i)}
                    >
                      {i}
                    </button>
                  ))}
                </Box>
                <Box className={classes.scaleLabels}>
                  <Typography className={classes.scaleLabel}>{labels.q10NotLikely}</Typography>
                  <Typography className={classes.scaleLabel}>{labels.q10ExtremelyLikely}</Typography>
                </Box>
              </Box>
            </Box>

            {/* Question 11: Free text (optional) */}
            <Box className={classes.questionBlock}>
              <Typography className={classes.questionLabel}>{labels.q11}</Typography>
              <TextField
                multiline
                minRows={2}
                maxRows={4}
                variant="outlined"
                placeholder={labels.q11Placeholder}
                value={q11}
                onChange={e => setQ11(e.target.value)}
                fullWidth
              />
            </Box>

            <Button
              variant="contained"
              color="primary"
              className={classes.submitButton}
              onClick={handleSubmit}
              disabled={!isValid || submitting}
            >
              {labels.submit}
            </Button>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
